import api from '@/libs/axiosInstance';
import type { Pagination } from '@/types/pagination';
import type { PayoutBatchListItem, PayoutBatchStatus } from '@fluenceur/types';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

export interface PayoutBatchesFilters {
  page?: number;
  limit?: number;
  status?: PayoutBatchStatus | null;
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
  hasInvoice?: boolean; // Filter to show only batches whose payment was accepted
}

export interface PayoutBatchesResponse {
  meta: Pagination;
  data: PayoutBatchListItem[];
}

const fetchPayoutBatches = async (
  filters: PayoutBatchesFilters
): Promise<PayoutBatchesResponse> => {
  // Construct query parameters, removing null/undefined values
  const params = Object.entries(filters).reduce(
    (acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, any>
  );

  // Actual API call using apiService
  const { data } = await api.get<PayoutBatchesResponse>('/dashboard/payouts', { params });
  return data;
};

export const usePayoutBatchesQuery = (
  filters: PayoutBatchesFilters,
  options?: { enabled?: boolean }
): UseQueryResult<PayoutBatchesResponse, Error> => {
  return useQuery<PayoutBatchesResponse, Error, PayoutBatchesResponse, unknown[]>({
    queryKey: ['payoutBatches', filters], // Query key includes filters for proper caching
    queryFn: () => fetchPayoutBatches(filters),
    enabled: options?.enabled,
    // keepPreviousData: true, // Consider if you want to show previous data while new data is loading
  });
};
